// src/sections/gamification/WheelSpinModal.jsx
// Phase 4: the level-up wheel as a MOMENT, on the plays design kit. The
// outcome was decided server-side (deterministic seeded spin recorded on
// gameProfiles.wheelSpins) — this modal is pure theater: the owner's rendered
// wheel RING / HUB / POINTER are static chrome while a config-driven wedge
// disc (labels, odds and reward-object icons all from the brand's slice
// config — prize text is never baked into art) spins ~5 eased turns inside
// the ring hole and lands on the winning slice; the prize then rides in on
// the shared reward-popup treatment. Rendered by CelebrationOverlay ahead of
// the generic celebration dialog.

import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';

import { downloadShareCard } from '../../utils/shareCard';
import { nightStage, Atmosphere, TopBar, StatusPill, Headline, GoldCTA, heroAssetUrl, NIGHT } from './plays/uiKit';
import { rewardArt, FloatingReward } from './plays/artKit';
import { particleBurst } from './plays/confetti';

const SPIN_MS = 4600;

// Wheel geometry: the ring render's transparent hole radius is ~67% of its
// half-width (measured from the asset) — the wedge disc tucks slightly under.
const RING_W = 330;
const HOLE_FRACTION = 0.668;
const DISC_R = (RING_W / 2) * HOLE_FRACTION + 7;

// Wedge fills per slice type (the assembled-preview palette the owner signed
// off on): violet XP, gold cash, teal perk, near-black nothing.
const WEDGE_FILL = { xp: '#4c2e8a', cash: '#8f6a16', perk: '#106076', nothing: '#221836' };

function polar(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180; // 0° = top
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

function slicePath(cx, cy, r, startDeg, endDeg) {
  const [x1, y1] = polar(cx, cy, r, startDeg);
  const [x2, y2] = polar(cx, cy, r, endDeg);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}

function sliceCaption(slice) {
  if (!slice) return '';
  if (slice.type === 'cash') return slice.label || `$${slice.amount || ''}`;
  if (slice.type === 'xp') return slice.label || `${slice.xp || ''} XP`;
  if (slice.type === 'perk') return slice.label || slice.perk?.label || 'Perk';
  return slice.label || 'Nothing';
}

// Slice → the outcome shape the shared reward art/headline helpers speak.
function sliceOutcome(slice) {
  if (!slice) return null;
  return { type: slice.type, amount: slice.amount, xp: slice.xp, perk: slice.perk, label: slice.label };
}

export default function WheelSpinModal({ slices, winningIndex, levelName, brandName, hostFirstName, onClose }) {
  const [spinning, setSpinning] = useState(false);
  const [done, setDone] = useState(false);
  const burstRef = useRef(null);

  const seg = 360 / Math.max(1, slices.length);
  // Land the CENTER of the winning slice under the top pointer, after five
  // full showman turns (slight jitter inside the slice so it doesn't look
  // suspiciously dead-centre).
  const targetRotation = useMemo(() => {
    const jitter = (seg / 2) * 0.6 * (Math.sin(winningIndex * 7.3) || 0.3);
    return 5 * 360 + (360 - (winningIndex * seg + seg / 2 + jitter));
  }, [winningIndex, seg]);

  useEffect(() => {
    const start = setTimeout(() => setSpinning(true), 350);
    const finish = setTimeout(() => setDone(true), SPIN_MS + 850);
    return () => {
      clearTimeout(start);
      clearTimeout(finish);
    };
  }, []);

  const winner = slices[winningIndex] || null;
  const won = winner && winner.type !== 'nothing';
  const outcome = sliceOutcome(winner);
  const art = outcome ? rewardArt(outcome) : null;

  // burst when the prize lands
  useEffect(() => {
    if (!done || !won || !burstRef.current) return undefined;
    const stop = particleBurst(burstRef.current, {
      x: burstRef.current.clientWidth / 2,
      y: burstRef.current.clientHeight * 0.38,
      count: winner?.type === 'cash' ? 180 : 130,
      power: 10
    });
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, won]);

  const ringUrl = heroAssetUrl('wheel-ring');
  const pointerUrl = heroAssetUrl('wheel-pointer');
  const hubUrl = heroAssetUrl('wheel-hub');

  return (
    <Dialog fullScreen open PaperProps={{ sx: { background: nightStage, color: NIGHT.white, overflow: 'hidden' } }}>
      <Atmosphere />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center'
        }}
      >
        <TopBar
          left={
            <StatusPill>
              <Typography sx={{ fontWeight: 800, fontSize: 11, letterSpacing: '0.22em', color: NIGHT.violetSoft }}>LEVEL UP</Typography>
            </StatusPill>
          }
          right={
            levelName ? (
              <StatusPill accent="rgba(245,198,68,0.5)">
                <Typography sx={{ fontWeight: 900, fontSize: 12, letterSpacing: '0.14em', color: NIGHT.goldHi }}>
                  {String(levelName).toUpperCase()}
                </Typography>
              </StatusPill>
            ) : null
          }
        />
        <Headline kicker="SPIN THE" title="WHEEL" sx={{ mb: 1 }} />
        <Typography sx={{ fontSize: 14, color: NIGHT.dim, mb: 3 }}>Every level-up earns one spin</Typography>

        {/* wheel assembly: static rendered ring + pointer + hub, spinning
            config-driven wedge disc tucked inside the ring hole */}
        <Box sx={{ position: 'relative', width: RING_W, height: RING_W, flexShrink: 0 }}>
          {/* glow pool behind the wheel */}
          <Box
            sx={{
              position: 'absolute',
              inset: -40,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(139,92,246,0.28) 0%, transparent 62%)',
              filter: 'blur(8px)'
            }}
          />
          {/* wedge disc (spins) */}
          <Box
            component="svg"
            viewBox="0 0 200 200"
            sx={{
              position: 'absolute',
              top: RING_W / 2 - DISC_R,
              left: RING_W / 2 - DISC_R,
              width: DISC_R * 2,
              height: DISC_R * 2,
              transform: `rotate(${spinning ? targetRotation : 0}deg)`,
              transition: spinning ? `transform ${SPIN_MS}ms cubic-bezier(0.12, 0.85, 0.16, 1)` : 'none'
            }}
          >
            <defs>
              <radialGradient id="wheel-shade" cx="0.5" cy="0.5" r="0.5">
                <stop offset="0" stopColor="rgba(255,255,255,0.16)" />
                <stop offset="0.55" stopColor="rgba(255,255,255,0)" />
                <stop offset="1" stopColor="rgba(0,0,0,0.45)" />
              </radialGradient>
            </defs>
            {slices.map((slice, i) => {
              const start = i * seg;
              const mid = start + seg / 2;
              const iconUrl = heroAssetUrl(rewardArt(sliceOutcome(slice)).asset);
              const [ix, iy] = polar(100, 100, 46, mid);
              const [tx, ty] = polar(100, 100, 79, mid);
              return (
                <g key={i}>
                  <path d={slicePath(100, 100, 100, start, start + seg)} fill={WEDGE_FILL[slice.type] || WEDGE_FILL.nothing} />
                  {iconUrl ? (
                    <image
                      href={iconUrl}
                      x={ix - 14}
                      y={iy - 14}
                      width="28"
                      height="28"
                      transform={`rotate(${mid}, ${ix}, ${iy})`}
                      style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))' }}
                    />
                  ) : null}
                  <text
                    x={tx}
                    y={ty}
                    fill="#fff"
                    fontSize="8.5"
                    fontWeight="800"
                    letterSpacing="0.5"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    transform={`rotate(${mid}, ${tx}, ${ty})`}
                    style={{ textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}
                  >
                    {sliceCaption(slice).slice(0, 14).toUpperCase()}
                  </text>
                </g>
              );
            })}
            {/* depth shading + hairline dividers over the wedges */}
            <circle cx="100" cy="100" r="100" fill="url(#wheel-shade)" />
            {slices.map((_, i) => {
              const [dx, dy] = polar(100, 100, 100, i * seg);
              return <line key={i} x1="100" y1="100" x2={dx} y2={dy} stroke="rgba(220,205,255,0.35)" strokeWidth="0.8" />;
            })}
          </Box>
          {/* ring (static chrome) */}
          {ringUrl ? (
            <Box component="img" src={ringUrl} alt="" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
          ) : (
            <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '10px solid rgba(245,198,68,0.8)' }} />
          )}
          {/* hub */}
          {hubUrl ? (
            <Box
              component="img"
              src={hubUrl}
              alt=""
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: DISC_R * 0.78,
                transform: 'translate(-50%, -50%)',
                filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.55))'
              }}
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 34,
                height: 34,
                borderRadius: '50%',
                transform: 'translate(-50%, -50%)',
                background: `linear-gradient(180deg, ${NIGHT.goldHi}, ${NIGHT.gold})`
              }}
            />
          )}
          {/* pointer */}
          {pointerUrl ? (
            <Box
              component="img"
              src={pointerUrl}
              alt=""
              sx={{
                position: 'absolute',
                top: -26,
                left: '50%',
                width: 54,
                transform: 'translateX(-50%)',
                zIndex: 2,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))'
              }}
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '13px solid transparent',
                borderRight: '13px solid transparent',
                borderTop: `20px solid ${NIGHT.gold}`,
                zIndex: 2,
                filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))'
              }}
            />
          )}
        </Box>

        {!done ? (
          <Button size="small" onClick={() => setDone(true)} sx={{ mt: 3, color: 'rgba(245,243,255,0.45)', letterSpacing: '0.1em' }}>
            SKIP
          </Button>
        ) : null}
      </Box>

      {/* prize reveal — the shared reward-popup treatment + Share */}
      {done && outcome ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            background: 'rgba(6,4,14,0.72)',
            backdropFilter: 'blur(8px)',
            animation: 'wheelPrizeFade 0.4s ease both',
            '@keyframes wheelPrizeFade': { from: { opacity: 0 }, to: { opacity: 1 } }
          }}
        >
          <Box
            component="canvas"
            ref={burstRef}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '18%',
              width: 360,
              height: 360,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${art ? art.glow : 'rgba(139,92,246,0.35)'} 0%, transparent 62%)`,
              filter: 'blur(6px)'
            }}
          />
          <Box
            sx={{
              position: 'relative',
              animation: 'wheelPrizeRise 0.65s cubic-bezier(0.16, 1.3, 0.36, 1) both',
              '@keyframes wheelPrizeRise': {
                from: { transform: 'translateY(42px) scale(0.82)', opacity: 0 },
                to: { transform: 'none', opacity: 1 }
              }
            }}
          >
            <FloatingReward outcome={outcome} size={160} />
            <Typography
              sx={{
                mt: 2.5,
                fontWeight: 800,
                fontSize: 13,
                letterSpacing: '0.42em',
                textIndent: '0.42em',
                color: won ? NIGHT.violetSoft : NIGHT.dim,
                textShadow: won ? '0 0 16px rgba(139,92,246,0.6)' : 'none'
              }}
            >
              {won ? 'WHEEL PRIZE' : 'NOT TONIGHT'}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                px: 3,
                fontWeight: 900,
                fontSize: { xs: 34, sm: 42 },
                letterSpacing: '0.06em',
                lineHeight: 1.05,
                color: winner?.type === 'cash' ? NIGHT.goldHi : NIGHT.white,
                textShadow:
                  winner?.type === 'cash'
                    ? '0 0 30px rgba(245,198,68,0.55), 0 2px 16px rgba(0,0,0,0.8)'
                    : '0 0 28px rgba(167,139,250,0.45), 0 2px 16px rgba(0,0,0,0.8)'
              }}
            >
              {won ? sliceCaption(winner).toUpperCase() : 'NO PRIZE THIS TIME'}
            </Typography>
            <Typography sx={{ mt: 1, fontSize: 15, color: NIGHT.dim }}>
              {won ? 'Already added to your account' : 'Next level, next spin'}
            </Typography>
            <Box sx={{ mt: 3.5 }}>
              <GoldCTA onClick={onClose}>{won ? 'COLLECT' : 'CLOSE'}</GoldCTA>
            </Box>
            {won ? (
              <Stack direction="row" justifyContent="center" sx={{ mt: 1.5 }}>
                <Button
                  variant="text"
                  sx={{ color: NIGHT.violetSoft, fontWeight: 700 }}
                  onClick={() =>
                    downloadShareCard({ brandName, hostFirstName, headline: sliceCaption(winner), subline: 'Level-up wheel win' })
                  }
                >
                  Share to story
                </Button>
              </Stack>
            ) : null}
          </Box>
        </Box>
      ) : null}
    </Dialog>
  );
}

WheelSpinModal.propTypes = {
  slices: PropTypes.array.isRequired,
  winningIndex: PropTypes.number.isRequired,
  levelName: PropTypes.string,
  brandName: PropTypes.string,
  hostFirstName: PropTypes.string,
  onClose: PropTypes.func.isRequired
};
